import { z } from "zod";
import { encodeApiPathSegment, type ApiRelativePath } from "./api-routes";
import { EntitlementTierCapabilityPolicySchema } from "./entitlement";

export const AdminPermissionSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)+$/);

export const AdminScopeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("platform"), id: z.null() }),
  z.object({ type: z.literal("app"), id: z.string().min(1).max(64) }),
]);

export const AdminAuthorizationGrantSchema = z.object({
  role: z.string().min(1).max(64),
  scope: AdminScopeSchema,
  permissions: z.array(AdminPermissionSchema),
});

export const AdminSessionSchema = z.object({
  principalId: z.string().uuid(),
  userId: z.string().uuid(),
  grants: z.array(AdminAuthorizationGrantSchema).min(1),
  permissions: z.array(AdminPermissionSchema),
  strongAuthentication: z.boolean(),
  authenticatedAt: z.string().datetime().nullable(),
});

export const AdminAuthConfigSchema = z.object({
  domain: z.string().min(1),
  clientId: z.string().min(1),
  audience: z.string().min(1),
  scope: z.string().min(1),
});

export const AdminPageQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce.number().int().min(1).max(100).default(25),
  sort: z.string().max(96).optional(),
});

export const AdminPageSchema = z.object({
  size: z.number().int().nonnegative(),
  number: z.number().int().positive(),
  totalElements: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
});

export const AdminGovernanceStatusSchema = z.enum([
  "healthy",
  "warning",
  "failure",
  "unavailable",
]);

export const AdminOverviewSchema = z.object({
  generatedAt: z.string().datetime(),
  source: z.literal("postgresql"),
  users: z.object({
    total: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
  }),
  apps: z.object({
    total: z.literal(13),
    visible: z.number().int().min(0).max(13),
    unavailableCapabilities: z.number().int().nonnegative(),
  }),
  entitlements: z.object({
    active: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
  }),
  jobs: z.object({
    deadLetter: z.number().int().nonnegative(),
    oldestOccurredAt: z.string().datetime().nullable(),
  }),
  providers: z.object({
    healthy: z.number().int().nonnegative(),
    degraded: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
  }),
  recentAuditEvents: z.number().int().nonnegative(),
});

export const AdminAppCapabilityStatusSchema = z.enum([
  "available",
  "degraded",
  "unavailable",
  "not_integrated",
  "not_applicable",
]);

export const AdminAppCapabilitySchema = z.object({
  key: z.string().min(1).max(128),
  status: AdminAppCapabilityStatusSchema,
  restrictedDataKey: z.string().min(1).max(160),
  revision: z.number().int().positive(),
  updatedAt: z.string().datetime(),
});

export const AdminCatalogPolicySchema = z.object({
  sortOrder: z.number().int().nonnegative(),
  featured: z.boolean(),
  visible: z.boolean(),
  notes: z.string().max(512),
  revision: z.number().int().positive(),
  updatedAt: z.string().datetime(),
});

export const AdminAppSchema = z.object({
  appId: z.string().min(1).max(64),
  status: z.enum(["active", "suspended", "retired"]),
  audience: z.enum(["public", "authenticated", "entitled"]),
  requiredTier: z.enum(["free", "plus", "pro"]),
  revision: z.number().int().positive(),
  policy: AdminCatalogPolicySchema,
  capabilities: z.array(AdminAppCapabilitySchema),
  releaseCount: z.number().int().nonnegative(),
  latestReleaseAt: z.string().datetime().nullable(),
  governanceStatus: AdminGovernanceStatusSchema,
});

export const AdminAppListSchema = z.object({
  content: z.array(AdminAppSchema),
  page: AdminPageSchema,
});

export const AdminAppParamsSchema = z.object({
  appId: z.string().min(1).max(64),
});

export const AdminCatalogPolicyUpdateSchema = z.object({
  sortOrder: z.number().int().min(0).max(100_000),
  featured: z.boolean(),
  visible: z.boolean(),
  notes: z.string().trim().max(512).default(""),
  revision: z.number().int().positive(),
  reason: z.string().trim().min(3).max(512),
  confirmed: z.literal(true),
});

export const AdminUserQuerySchema = AdminPageQuerySchema.extend({
  query: z.string().trim().max(128).optional(),
  status: z.enum(["active", "banned", "all"]).default("all"),
});

export const AdminUserSchema = z.object({
  userId: z.string().uuid(),
  username: z.string().min(1),
  displayName: z.string().min(1),
  email: z.string().email().nullable(),
  accountStatus: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  revision: z.number().int().positive(),
  activeEntitlements: z.number().int().nonnegative(),
  activeSessionCount: z.number().int().nonnegative().nullable(),
  rolloutCohorts: z.array(z.string().regex(/^[a-z][a-z0-9_.-]{0,63}$/)),
});

export const AdminUserListSchema = z.object({
  content: z.array(AdminUserSchema),
  page: AdminPageSchema,
});

export const AdminUserParamsSchema = z.object({
  userId: z.string().uuid(),
});

export const AdminRolloutCohortKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_.-]{0,63}$/);

export const AdminUserRolloutCohortsUpdateSchema = z.object({
  cohortKeys: z.array(AdminRolloutCohortKeySchema).max(16),
  reason: z.string().trim().min(3).max(512),
  revision: z.number().int().positive(),
  confirmed: z.literal(true),
});

export const AdminUserRolloutCohortsMutationResultSchema = z.object({
  userId: z.string().uuid(),
  rolloutCohorts: z.array(AdminRolloutCohortKeySchema),
  revision: z.number().int().positive(),
  auditEventId: z.string().uuid(),
});

export const AdminConfirmedMutationSchema = z.object({
  reason: z.string().trim().min(3).max(512),
  revision: z.number().int().positive(),
  confirmed: z.literal(true),
});

export const AdminStewardRoomAccessListSchema = z.object({
  userId: z.string().uuid(),
  revision: z.number().int().positive(),
  rooms: z.array(z.object({ roomId: z.string().min(1).max(64), role: z.enum(["owner", "viewer"]) })).max(1000),
});
export const AdminStewardRoomAccessUpdateSchema = AdminConfirmedMutationSchema.extend({
  roomId: z.string().trim().regex(/^[A-Za-z0-9-]{1,64}$/),
  role: z.enum(["owner", "viewer"]).nullable(),
});
export const AdminStewardRoomAccessMutationSchema = AdminStewardRoomAccessListSchema.extend({ auditEventId: z.string().uuid() });
export type AdminStewardRoomAccessUpdate = z.infer<typeof AdminStewardRoomAccessUpdateSchema>;

export const AdminSuspensionSchema = z.object({
  userId: z.string().uuid(),
  status: z.literal("banned"),
  reason: z.string().min(3).max(512),
  revision: z.number().int().positive(),
  auditEventId: z.string().uuid(),
});

export const AdminRestorationSchema = AdminSuspensionSchema.extend({
  status: z.literal("active"),
});

export const AdminSessionRevocationSchema = z.object({
  userId: z.string().uuid(),
  revokedAt: z.string().datetime(),
  auditEventId: z.string().uuid(),
});

export const AdminEntitlementQuerySchema = AdminPageQuerySchema.extend({
  userId: z.string().uuid().optional(),
  appId: z.string().min(1).max(64).optional(),
  status: z.enum(["active", "expired", "all"]).default("all"),
});

export const AdminEntitlementSchema = z.object({
  entitlementId: z.string(),
  userId: z.string().uuid(),
  appId: z.string().min(1).max(64),
  tier: z.enum(["free", "plus", "pro"]),
  source: z.string().min(1),
  startsAt: z.string(),
  expiresAt: z.string().nullable(),
  status: z.enum(["active", "expired"]),
  revision: z.number().int().positive(),
});

export const AdminEntitlementListSchema = z.object({
  content: z.array(AdminEntitlementSchema),
  page: AdminPageSchema,
});

export const AdminEntitlementParamsSchema = z.object({
  entitlementId: z.string().min(1).max(128),
});

export const AdminEntitlementUpdateSchema = z
  .object({
    tier: z.enum(["free", "plus", "pro"]),
    startsAt: z.iso.date(),
    expiresAt: z.iso.date().nullable(),
    revision: z.number().int().positive(),
    reason: z.string().trim().min(3).max(512),
    confirmed: z.literal(true),
  })
  .refine((value) => !value.expiresAt || value.expiresAt >= value.startsAt, {
    path: ["expiresAt"],
    message: "Expiration date must not precede the start date",
  });

export const AdminTierCapabilityPolicySchema =
  EntitlementTierCapabilityPolicySchema.and(
    z.object({
      appId: z.string().min(1).max(64),
      revision: z.number().int().positive(),
      updatedAt: z.string().datetime(),
    }),
  );

export const AdminTierCapabilityPolicyQuerySchema = AdminPageQuerySchema.extend(
  {
    appId: z.string().min(1).max(64).optional(),
  },
);

export const AdminTierCapabilityPolicyListSchema = z.object({
  content: z.array(AdminTierCapabilityPolicySchema),
  page: AdminPageSchema,
});

export const AdminTierCapabilityPolicyParamsSchema = z.object({
  appId: z.string().min(1).max(64),
  capabilityKey: z.string().min(1).max(128),
});

const AdminTierCapabilityMutationMetadataSchema = z.object({
  revision: z.number().int().positive(),
  reason: z.string().trim().min(3).max(512),
  confirmed: z.literal(true),
});

export const AdminTierCapabilityPolicyUpdateSchema = z.discriminatedUnion(
  "kind",
  [
    AdminTierCapabilityMutationMetadataSchema.extend({
      kind: z.literal("boolean"),
      plus: z.boolean(),
      pro: z.boolean(),
    }).refine((value) => !value.plus || value.pro, {
      path: ["pro"],
      message: "Pro must include every Plus boolean capability",
    }),
    AdminTierCapabilityMutationMetadataSchema.extend({
      kind: z.literal("limit"),
      plus: z.number().int().nonnegative().nullable(),
      pro: z.number().int().nonnegative().nullable(),
    }).refine(
      (value) =>
        value.pro === null || (value.plus !== null && value.pro >= value.plus),
      {
        path: ["pro"],
        message: "Pro limit must be unlimited or no lower than Plus",
      },
    ),
  ],
);

export const AdminTierCapabilityPolicyMutationResultSchema = z.object({
  policy: AdminTierCapabilityPolicySchema,
  auditEventId: z.string().uuid(),
});

export const AdminJobSourceSchema = z.object({
  source: z.string().min(1),
  count: z.number().int().nonnegative(),
});

export const AdminJobSummarySchema = z.object({
  deadLetterCount: z.number().int().nonnegative(),
  oldestOccurredAt: z.string().datetime().nullable(),
  sources: z.array(AdminJobSourceSchema),
  generatedAt: z.string().datetime(),
});

export const AdminJobReplaySchema = z.object({
  source: z.string().min(1).max(128),
  limit: z.number().int().min(1).max(100),
  reason: z.string().trim().min(3).max(512),
  confirmed: z.literal(true),
});

export const AdminJobReplayResultSchema = z.object({
  source: z.string().min(1),
  replayedCount: z.number().int().nonnegative(),
  duplicateCount: z.number().int().nonnegative(),
  invalidCount: z.number().int().nonnegative(),
  auditEventId: z.string().uuid(),
});

export const AdminProviderSchema = z.object({
  appId: z.string().min(1).max(64),
  providerKey: z.string().min(1).max(128),
  status: z.enum(["healthy", "degraded", "unavailable", "unknown"]),
  configurationState: z.enum(["configured", "unconfigured", "disabled"]),
  fingerprint: z.string().nullable(),
  lastCheckedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
});

export const AdminProviderListSchema = z.object({
  providers: z.array(AdminProviderSchema),
  generatedAt: z.string().datetime(),
});

export const AdminAuditQuerySchema = AdminPageQuerySchema.extend({
  actorUserId: z.string().uuid().optional(),
  appId: z.string().min(1).max(64).optional(),
  action: z.string().min(1).max(128).optional(),
  result: z.enum(["succeeded", "failed", "denied"]).optional(),
});

export const AdminAuditEventSchema = z.object({
  eventId: z.string().uuid(),
  occurredAt: z.string().datetime(),
  actorUserId: z.string().uuid().nullable(),
  action: z.string().min(1),
  targetType: z.string().min(1),
  targetId: z.string().min(1),
  appId: z.string().nullable(),
  requestId: z.string().min(1),
  reason: z.string(),
  result: z.enum(["succeeded", "failed", "denied"]),
});

export const AdminAuditEventListSchema = z.object({
  content: z.array(AdminAuditEventSchema),
  page: AdminPageSchema,
});

export const AdminFeatureFlagValueTypeSchema = z.enum([
  "boolean",
  "string",
  "number",
  "json",
]);

export const AdminFeatureFlagRuleSchema = z.object({
  ruleId: z.string().uuid(),
  enabled: z.boolean(),
  value: z.unknown(),
  scopeType: z.enum([
    "global",
    "platform",
    "app-version",
    "percentage",
    "cohort",
  ]),
  scopeValue: z.string().nullable(),
  rolloutPercentage: z.number().int().min(0).max(100),
  priority: z.number().int(),
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
  revision: z.number().int().positive(),
});

export const AdminFeatureFlagSchema = z.object({
  appId: z.string().min(1).max(64),
  flagKey: z.string().regex(/^[a-z][a-z0-9_.-]{0,95}$/),
  valueType: AdminFeatureFlagValueTypeSchema,
  defaultValue: z.unknown(),
  descriptionKey: z.string().min(1).max(160),
  ownerUserId: z.string().uuid().nullable(),
  expiresAt: z.string().datetime().nullable(),
  status: z.enum(["active", "archived"]),
  revision: z.number().int().positive(),
  rules: z.array(AdminFeatureFlagRuleSchema),
});

export const AdminFeatureFlagListSchema = z.object({
  flags: z.array(AdminFeatureFlagSchema),
});

export const AdminFeatureFlagHistorySchema = z.object({
  content: z.array(
    z.object({
      eventId: z.string().uuid(),
      occurredAt: z.string().datetime(),
      actorUserId: z.string().uuid().nullable(),
      reason: z.string(),
      definition: AdminFeatureFlagSchema.omit({ rules: true }),
    }),
  ),
  page: AdminPageSchema,
});

export const AdminCatalogPolicyHistorySchema = z.object({
  content: z.array(
    z.object({
      eventId: z.string().uuid(),
      occurredAt: z.string().datetime(),
      actorUserId: z.string().uuid().nullable(),
      reason: z.string(),
      policy: AdminCatalogPolicySchema,
    }),
  ),
  page: AdminPageSchema,
});

export const AdminFeatureFlagRuleHistorySchema = z.object({
  content: z.array(
    z.object({
      eventId: z.string().uuid(),
      occurredAt: z.string().datetime(),
      actorUserId: z.string().uuid().nullable(),
      reason: z.string(),
      rule: AdminFeatureFlagRuleSchema,
    }),
  ),
  page: AdminPageSchema,
});

export const AdminFeatureFlagUpsertSchema = z.object({
  valueType: AdminFeatureFlagValueTypeSchema,
  defaultValue: z.unknown(),
  descriptionKey: z.string().min(1).max(160),
  ownerUserId: z.string().uuid().nullable(),
  expiresAt: z.string().datetime().nullable(),
  status: z.enum(["active", "archived"]),
  revision: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(512),
  confirmed: z.literal(true),
});

export const AdminFeatureFlagRuleParamsSchema = AdminAppParamsSchema.extend({
  flagKey: z.string().regex(/^[a-z][a-z0-9_.-]{0,95}$/),
  ruleId: z.string().uuid(),
});

export const AdminFeatureFlagRuleUpsertSchema = z
  .object({
    enabled: z.boolean(),
    value: z.unknown(),
    scopeType: z.enum([
      "global",
      "platform",
      "app-version",
      "percentage",
      "cohort",
    ]),
    scopeValue: z.string().trim().min(1).max(128).nullable(),
    rolloutPercentage: z.number().int().min(0).max(100),
    priority: z.number().int().min(-100_000).max(100_000),
    startsAt: z.string().datetime().nullable(),
    endsAt: z.string().datetime().nullable(),
    revision: z.number().int().nonnegative(),
    reason: z.string().trim().min(3).max(512),
    confirmed: z.literal(true),
  })
  .superRefine((value, context) => {
    const needsScopeValue = ["platform", "app-version", "cohort"].includes(
      value.scopeType,
    );
    if (needsScopeValue !== Boolean(value.scopeValue)) {
      context.addIssue({
        code: "custom",
        path: ["scopeValue"],
        message: "Scope value does not match the selected scope type",
      });
    }
    if (
      value.scopeType === "platform" &&
      !["web", "desktop", "ios", "android"].includes(value.scopeValue ?? "")
    ) {
      context.addIssue({
        code: "custom",
        path: ["scopeValue"],
        message: "Platform scope must use a supported client platform",
      });
    }
    if (value.startsAt && value.endsAt && value.endsAt <= value.startsAt) {
      context.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "Rule end time must follow its start time",
      });
    }
  });

export const AdminFeatureFlagMutationResultSchema = z.object({
  flag: AdminFeatureFlagSchema,
  auditEventId: z.string().uuid(),
});

export const AdminFeatureFlagRuleMutationResultSchema = z.object({
  rule: AdminFeatureFlagRuleSchema,
  auditEventId: z.string().uuid(),
});

export const EffectiveAppGovernanceQuerySchema = z.object({
  platform: z.enum(["web", "desktop", "ios", "android"]),
  appVersion: z.string().min(1).max(64),
});

export const EffectiveAppGovernanceSchema = z.object({
  generatedAt: z.string().datetime(),
  revision: z.number().int().nonnegative(),
  apps: z.array(
    z.object({
      appId: z.string().min(1).max(64),
      sortOrder: z.number().int().nonnegative(),
      catalogRevision: z.number().int().nonnegative().optional(),
      featured: z.boolean(),
      visible: z.boolean(),
      flags: z.record(
        z.string(),
        z.object({
          value: z.unknown(),
          source: z.string().min(1),
          evaluatedAt: z.string().datetime(),
          definitionRevision: z.number().int().positive().optional(),
          ruleRevision: z.number().int().positive().nullable().optional(),
        }),
      ),
    }),
  ),
});

export const AdminApiPath = {
  UserStewardRooms: "/admin/users/:userId/steward-rooms",
  UserStewardRoomsUpdate: "/admin/users/:userId/steward-rooms/update",
  AuthConfig: "/admin/auth/config",
  Session: "/admin/session",
  Overview: "/admin/overview",
  PlatformMarketApps: "/admin/platform/market/apps",
  PlatformMarketAppCatalogPolicyUpdate:
    "/admin/platform/market/apps/:appId/catalog-policy/update",
  PlatformMarketAppCatalogPolicyHistory:
    "/admin/platform/market/apps/:appId/catalog-policy/history",
  Apps: "/admin/apps",
  App: "/admin/apps/:appId",
  AppFeatureFlags: "/admin/apps/:appId/feature-flags",
  AppFeatureFlagHistory: "/admin/apps/:appId/feature-flags/:flagKey/history",
  AppFeatureFlagUpsert: "/admin/apps/:appId/feature-flags/:flagKey/upsert",
  AppFeatureFlagRuleUpsert:
    "/admin/apps/:appId/feature-flags/:flagKey/rules/:ruleId/upsert",
  AppFeatureFlagRuleHistory:
    "/admin/apps/:appId/feature-flags/:flagKey/rules/:ruleId/history",
  Users: "/admin/users",
  UserSuspensionApply: "/admin/users/:userId/suspension/apply",
  UserSuspensionClear: "/admin/users/:userId/suspension/clear",
  UserSessionsRevoke: "/admin/users/:userId/sessions/revoke",
  UserRolloutCohortsUpdate: "/admin/users/:userId/rollout-cohorts/update",
  Entitlements: "/admin/entitlements",
  EntitlementUpdate: "/admin/entitlements/:entitlementId/update",
  TierCapabilityPolicies: "/admin/platform/tier-capability-policies",
  TierCapabilityPolicyUpdate:
    "/admin/platform/tier-capability-policies/:appId/:capabilityKey/update",
  Jobs: "/admin/jobs",
  JobAttempts: "/admin/jobs/:source/attempts",
  Providers: "/admin/providers",
  AuditEvents: "/admin/audit-events",
  EffectiveAppGovernance: "/app-governance/effective-config",
} as const satisfies Record<string, ApiRelativePath>;

export const adminApiPath = {
  userStewardRooms: (userId: string): ApiRelativePath => `/admin/users/${encodeApiPathSegment(userId)}/steward-rooms`,
  userStewardRoomsUpdate: (userId: string): ApiRelativePath => `/admin/users/${encodeApiPathSegment(userId)}/steward-rooms/update`,
  app: (appId: string): ApiRelativePath =>
    `/admin/apps/${encodeApiPathSegment(appId)}`,
  platformMarketAppCatalogPolicyUpdate: (appId: string): ApiRelativePath =>
    `/admin/platform/market/apps/${encodeApiPathSegment(appId)}/catalog-policy/update`,
  platformMarketAppCatalogPolicyHistory: (appId: string): ApiRelativePath =>
    `/admin/platform/market/apps/${encodeApiPathSegment(appId)}/catalog-policy/history`,
  appFeatureFlagRuleHistory: (
    appId: string,
    flagKey: string,
    ruleId: string,
  ): ApiRelativePath =>
    `/admin/apps/${encodeApiPathSegment(appId)}/feature-flags/${encodeApiPathSegment(flagKey)}/rules/${encodeApiPathSegment(ruleId)}/history`,
  appFeatureFlags: (appId: string): ApiRelativePath =>
    `/admin/apps/${encodeApiPathSegment(appId)}/feature-flags`,
  appFeatureFlagHistory: (appId: string, flagKey: string): ApiRelativePath =>
    `/admin/apps/${encodeApiPathSegment(appId)}/feature-flags/${encodeApiPathSegment(flagKey)}/history`,
  appFeatureFlagUpsert: (appId: string, flagKey: string): ApiRelativePath =>
    `/admin/apps/${encodeApiPathSegment(appId)}/feature-flags/${encodeApiPathSegment(flagKey)}/upsert`,
  appFeatureFlagRuleUpsert: (
    appId: string,
    flagKey: string,
    ruleId: string,
  ): ApiRelativePath =>
    `/admin/apps/${encodeApiPathSegment(appId)}/feature-flags/${encodeApiPathSegment(flagKey)}/rules/${encodeApiPathSegment(ruleId)}/upsert`,
  userSuspensionApply: (userId: string): ApiRelativePath =>
    `/admin/users/${encodeApiPathSegment(userId)}/suspension/apply`,
  userSuspensionClear: (userId: string): ApiRelativePath =>
    `/admin/users/${encodeApiPathSegment(userId)}/suspension/clear`,
  userSessionsRevoke: (userId: string): ApiRelativePath =>
    `/admin/users/${encodeApiPathSegment(userId)}/sessions/revoke`,
  userRolloutCohortsUpdate: (userId: string): ApiRelativePath =>
    `/admin/users/${encodeApiPathSegment(userId)}/rollout-cohorts/update`,
  entitlementUpdate: (entitlementId: string): ApiRelativePath =>
    `/admin/entitlements/${encodeApiPathSegment(entitlementId)}/update`,
  tierCapabilityPolicyUpdate: (
    appId: string,
    capabilityKey: string,
  ): ApiRelativePath =>
    `/admin/platform/tier-capability-policies/${encodeApiPathSegment(appId)}/${encodeApiPathSegment(capabilityKey)}/update`,
  jobAttempts: (source: string): ApiRelativePath =>
    `/admin/jobs/${encodeApiPathSegment(source)}/attempts`,
} as const;

export type AdminPermission = z.infer<typeof AdminPermissionSchema>;
export type AdminScope = z.infer<typeof AdminScopeSchema>;
export type AdminAuthorizationGrant = z.infer<
  typeof AdminAuthorizationGrantSchema
>;
export type AdminSession = z.infer<typeof AdminSessionSchema>;
export type AdminOverview = z.infer<typeof AdminOverviewSchema>;
export type AdminApp = z.infer<typeof AdminAppSchema>;
export type AdminCatalogPolicyUpdate = z.infer<
  typeof AdminCatalogPolicyUpdateSchema
>;
export type AdminUserQuery = z.infer<typeof AdminUserQuerySchema>;
export type AdminUserRolloutCohortsUpdate = z.infer<
  typeof AdminUserRolloutCohortsUpdateSchema
>;
export type AdminConfirmedMutation = z.infer<
  typeof AdminConfirmedMutationSchema
>;
export type AdminEntitlementQuery = z.infer<typeof AdminEntitlementQuerySchema>;
export type AdminEntitlementUpdate = z.infer<
  typeof AdminEntitlementUpdateSchema
>;
export type AdminTierCapabilityPolicyQuery = z.infer<
  typeof AdminTierCapabilityPolicyQuerySchema
>;
export type AdminTierCapabilityPolicyUpdate = z.infer<
  typeof AdminTierCapabilityPolicyUpdateSchema
>;
export type AdminAuditQuery = z.infer<typeof AdminAuditQuerySchema>;
export type AdminFeatureFlagUpsert = z.infer<
  typeof AdminFeatureFlagUpsertSchema
>;
export type AdminFeatureFlagRuleUpsert = z.infer<
  typeof AdminFeatureFlagRuleUpsertSchema
>;
export type EffectiveAppGovernanceQuery = z.infer<
  typeof EffectiveAppGovernanceQuerySchema
>;
